import React, { useEffect, useState } from "react";

import {
  TFunction,
  formatNumber,
  getLocalStorageShowFieldRecommendations,
  isFieldEnrolled,
  useGeneralContext,
  useGetCarbonProgramByFarmId,
  useGetCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldCropRecommendations,
  useGetFieldDetails,
  useGetFieldPractices,
  useLocation,
  useNavigate,
  useParams,
  useTranslation,
  useUserContext,
} from "utils";

import { ACCOUNTING_METHODOLOGY, FieldDetails, PracticeBundle, VIEW_TYPE } from "models";

import { Flex, FlexPageSpin, HideFCPOFeatureForUnenrolledFarms, Icon, MainPage, PageSpin } from "components";

import { CropMixComposer } from "pages/app/crop-mix-composer/CropMixComposer.tsx";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { FieldTimelineSimulationsOverlay } from "pages/app/field-timeline-simulations/FieldTimelineSimulationsOverlay";
import { FieldDetailsTimeline } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { FieldTimelineHeader } from "pages/app/field-timeline/components/FieldTimelineHeader";
import { InterventionImpactPanel } from "pages/app/field-timeline/components/InterventionImpactPanel";

export const AdditionalData = ({ t, fieldDetails, isEnrolled }: { t: TFunction; fieldDetails: FieldDetails; isEnrolled: boolean }) => (
  <Flex className="additional-data" data-testid="additional-data" gap=".25rem">
    {fieldDetails && Object.keys(fieldDetails).length ? (
      <>
        {isEnrolled ? <Icon className="enrollment-icon" dataTestId="enrollment-icon" iconName="klim/fcpo-enrollment" /> : <></>}
        <span className="size-label paragraph-m" data-testid="size-label">{`${formatNumber({ number: fieldDetails?.size || 0, decimalPlaces: 2 })} ha`}</span>
        {fieldDetails.isInRedzone ? (
          <span className="is-redzone-label paragraph-m" data-testid="is-redzone-label">
            &#183; {t("header.isInRedzone")}
          </span>
        ) : (
          <></>
        )}
      </>
    ) : (
      <></>
    )}
  </Flex>
);

export const FieldTimelinePage = () => {
  const { t } = useTranslation("fieldTimeline");
  const { fieldId } = useParams();
  const { user } = useUserContext();
  const { currentFarm } = useGetFarms();
  const location = useLocation();
  const navigate = useNavigate();
  const [showRecommendations, setShowRecommendations] = useState(getLocalStorageShowFieldRecommendations() ?? true);
  const [hasMissingData, setHasMissingData] = useState<boolean>(false);
  const [navigateToMissingData, setNavigateToMissingData] = useState<boolean>(false);
  const [showSimulations, setShowSimulations] = useState<boolean>(false);
  const [showCropMixComposer, setShowCropMixComposer] = useState<boolean>(false);
  const isSimulationsPath = location.pathname.includes("/simulations");
  const isCropMixComposerPath = location.pathname.includes("/cmc");

  const { data: fieldDetails, isPending: isPendingFieldDetails } = useGetFieldDetails({ id: fieldId || "" });

  const { data: activeFarmReportingYear, isLoading: isFarmReportingYearLoading } = useGetFarmReportingYear(currentFarm?.id || null);

  const { data: fieldPractices, isPending: isPendingFieldPractices } = useGetFieldPractices({ fieldId: fieldId || "", inView: true });
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm?.id ?? undefined);
  const { data: carbonProgramOutcomes, isFetching: isFetchingCarbonProgramOutcomes } = useGetCarbonProgramOutcomes({
    fieldIds: fieldId ? [fieldId] : [],
    enabled: !!carbonProgram,
  });
  const { isFetching: isFetchingCropRecommendations } = useGetFieldCropRecommendations({
    fieldIds: fieldId ? [fieldId] : [],
    enabled: !!carbonProgram,
  });

  const [practiceBundles, setPracticeBundles] = useState<PracticeBundle[]>();
  const { setAppNavSideCollapsed } = useGeneralContext();
  const { selectedRecommendationItemOnTimeline } = useCropMixComposerContext();

  const fieldHasActivePractices = !!fieldPractices?.availablePracticeBundles.some((bundle) => bundle.active);
  const isHectareLimitReached = !fieldId || (fieldDetails?.exceedsHectareLimit && !fieldHasActivePractices);
  const isPracticeEnrollmentDisabled = !!(isHectareLimitReached || !carbonProgram || fieldPractices?.fieldHasFailedVerification);
  const fieldIsEnrolled = isFieldEnrolled(carbonProgramOutcomes?.[fieldId ?? ""]);

  useEffect(() => {
    if (isPendingFieldPractices) return;
    setPracticeBundles(fieldPractices?.availablePracticeBundles);
  }, [isPendingFieldPractices, fieldPractices?.availablePracticeBundles]);

  useEffect(() => {
    setAppNavSideCollapsed(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname.includes("/cmc") && !selectedRecommendationItemOnTimeline) {
      const basePath = location.pathname.split("/cmc").slice(0, -1).join("/");
      navigate(basePath);
    }
  }, [location.pathname, navigate, selectedRecommendationItemOnTimeline]);

  const navigationCrumbs = {
    crumbs: [
      { name: t("global:header.home"), link: "/home" },
      { name: user?.farm?.name || t("modules:navigation.overview.farm"), link: "/farm" },
      { name: t("fieldTimeline:header.fieldManagementCrumb"), link: "/all-fields" },
      { name: fieldDetails?.name || "" },
    ],
    additionalData: <AdditionalData fieldDetails={fieldDetails ?? ({} as FieldDetails)} isEnrolled={fieldIsEnrolled} t={t} />,
  };

  if (isPendingFieldPractices || !practiceBundles) return;

  return (
    <MainPage fullWidth pageName="field-timeline-page" share={{ title: t("pageTitle") }} navigationCrumbs={navigationCrumbs} showMobileAlert>
      {fieldId && fieldDetails && practiceBundles ? (
        <Flex className="field-timeline container" data-testid="field-timeline-page-container" justify="space-between">
          <Flex vertical className="main-container">
            <FieldTimelineHeader
              className="field-timeline-header header"
              dataTestId="field-timeline-header"
              fieldDetails={fieldDetails}
              availablePracticeBundles={practiceBundles}
              showRecommendations={showRecommendations}
              setShowRecommendations={setShowRecommendations}
              isFetchingCropRecommendations={isFetchingCropRecommendations}
              isEnrolled={fieldIsEnrolled}
              isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
              setPracticeBundles={setPracticeBundles}
              setShowSimulations={setShowSimulations}
              isPendingFieldDetails={isPendingFieldDetails}
            />
            {fieldDetails && !isPendingFieldDetails && (
              <div className="timeline-container">
                {isFarmReportingYearLoading ? (
                  <FlexPageSpin />
                ) : (
                  <FieldDetailsTimeline
                    fieldDetails={fieldDetails}
                    showRecommendations={showRecommendations}
                    isEnrolled={fieldIsEnrolled}
                    carbonProgram={carbonProgram ?? null}
                    setHasMissingData={setHasMissingData}
                    navigateToMissingData={navigateToMissingData}
                    setNavigateToMissingData={setNavigateToMissingData}
                    reportingYear={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                  />
                )}
              </div>
            )}
          </Flex>
          <HideFCPOFeatureForUnenrolledFarms>
            <Flex className="sidebar" vertical={true}>
              {isFarmReportingYearLoading ? (
                <FlexPageSpin />
              ) : (
                <>
                  {currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? null : (
                    <InterventionImpactPanel
                      carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
                      progress={isFetchingCarbonProgramOutcomes ? 0 : 100}
                      fieldSizes={{ [fieldDetails.id]: fieldDetails.size }}
                      viewType={VIEW_TYPE.SINGLE_FIELD}
                      hasMissingData={hasMissingData}
                      setNavigateToMissingData={setNavigateToMissingData}
                      practiceBundles={practiceBundles}
                      hidePayouts={carbonProgram?.hidePayouts}
                      reportingYearFromProps={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                    />
                  )}
                </>
              )}
            </Flex>
          </HideFCPOFeatureForUnenrolledFarms>
        </Flex>
      ) : (
        <PageSpin />
      )}
      {!!fieldId && (
        <FieldTimelineSimulationsOverlay
          visible={isSimulationsPath || showSimulations}
          setShowSimulations={setShowSimulations}
          carbonProgram={carbonProgram ?? null}
          fieldId={fieldId}
          isFetchingCropRecommendations={isFetchingCropRecommendations}
        />
      )}
      <CropMixComposer
        visible={showCropMixComposer || isCropMixComposerPath}
        setShowCropMixComposer={setShowCropMixComposer}
        regionId={fieldDetails?.regionId ?? ""}
        fieldId={fieldId}
        openInDrawer
      />
    </MainPage>
  );
};
